;(function optimizeCriticalResources() {
  const projectAlias = import.meta.env.VITE_PROJECT_ALIAS

  const criticalFonts = [
    { name: 'BasierCircle-Regular.woff2', weight: 400 },
    { name: 'BasierCircle-Medium.woff2', weight: 500 }
  ]

  // Preload only in dev mode; Vite handles optimization itself in production
  if (import.meta.env.DEV) {
    criticalFonts.forEach((font) => {
      const link = document.createElement('link')
      link.rel = 'preload'
      link.as = 'font'
      link.type = 'font/woff2'
      link.crossOrigin = 'anonymous'
      link.href = `/src/assets/${projectAlias}/fonts/${font.name}`
      document.head.appendChild(link)
    })
  }
})()

const stylesReady = Promise.allSettled([
  import('./assets/main.css'),
  import('~project_assets/project_style.css')
])

import { createApp, nextTick } from 'vue'
import App from '@/App.vue'
import router from '@/router/index.js'
import storage from '@/plugins/storage'
import appAxios from '@/plugins/axios'
import utils from '@/plugins/utils'
import { init, setTag, captureException, vueRouterInstrumentation } from '@sentry/vue'
import { BrowserTracing } from '@sentry/browser'
import { createPinia } from 'pinia'
import { piniaSentryPlugin } from '@/plugins/piniaSentry'
import { createGtm } from '@gtm-support/vue-gtm'
import loadGoogleMapsApi from 'load-google-maps-api'
import VueGoogleMaps from '@fawmi/vue-google-maps'
import logAppVersion from '@/plugins/appVersion'
import vSafeHtml from '@/directives/safeHtml'
import { APP_URL } from '@/utils/config'
import { chunkErrorDetected, isChunkLoadError } from '@/utils/chunkError'

logAppVersion()

// Global handler for dynamic chunk loading errors (fixes "Failed to fetch dynamically imported module")
// router.onError handles the same errors — this catches vite:preloadError and cases outside routing
let isProcessingError = false
const handleChunkError = (e) => {
  if (isProcessingError) return

  if (isChunkLoadError(e)) {
    e.preventDefault()
    isProcessingError = true
    chunkErrorDetected.value = true
    isProcessingError = false
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', (e) => handleChunkError(e))
  window.addEventListener('error', (e) => handleChunkError(e), true)
  window.addEventListener('unhandledrejection', (e) => handleChunkError(e))

  // Zoho PageSense is injected via a raw <script> tag in index.html (no build-time
  // instrumentation available there) — it dispatches this event on load failure.
  window.addEventListener('pagesense:load-error', () => {
    captureException(new Error('Zoho PageSense script failed to load'), {
      tags: { integration: 'pagesense' }
    })
  })
}

const pinia = createPinia()
pinia.use(piniaSentryPlugin)
const app = createApp(App)

app.directive('safe-html', vSafeHtml)

/**
 * Plugin install
 */
app.use(router)
app.use(pinia)
app.use(appAxios, {
  baseUrl: import.meta.env.VITE_APP_API_URL
})

// Dynamic loading of VueTelInput - loaded only when needed
let vueTelInputLoaded = false
const loadVueTelInput = async () => {
  if (vueTelInputLoaded) return
  vueTelInputLoaded = true
  const VueTelInput = (await import('vue-tel-input')).default
  const globalOptions = {
    mode: 'international',
    inputOptions: {
      placeholder: 'Mobile Phone Number',
      autocomplete: 'off'
    },
    autoFormat: false,
    dropdownOptions: [
      {
        showDialCodeInSelection: true,
        showFlags: true
      }
    ]
  }
  app.use(VueTelInput, globalOptions)
}

// Expose the function for use in the router
if (typeof window !== 'undefined') {
  window.__loadVueTelInput = loadVueTelInput
}

const loadAnalytics = () => {
  if (import.meta.env.VITE_GTM_ID) {
    app.use(
      createGtm({
        id: import.meta.env.VITE_GTM_ID,
        defer: true,
        compatibility: false,
        enabled: true,
        debug: true,
        vueRouter: router
      })
    )
  }
}

/**
 * Provides install
 */
app.provide('storage', storage)
app.provide('utils', utils)

const regexLink =
  /^(?!.*(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})).*$/g
const regexIsHttps = /^(?!.*ftp|https|http|www).*$/g
const regexNameField = /^(?!.*[!@#$%^*_<>]).*$/g
const regexEmail =
  /^[_a-zA-Z0-9-]+(\.[_a-zA-Z0-9-]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*(\.[a-zA-Z]{2,3})$/g

app.provide('regexLink', regexLink)
app.provide('regexIsHttps', regexIsHttps)
app.provide('regexNameField', regexNameField)
app.provide('regexEmail', regexEmail)

if (import.meta.env.VITE_APP_ENV !== 'local') {
  const isProduction = import.meta.env.MODE === 'production' || import.meta.env.MODE === 'staging'

  init({
    app,
    environment: import.meta.env.VITE_APP_ENV,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    // Build SHA injected by vite.config.js (define); empty outside a real build.
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    ignoreErrors: [
      'Blocked a frame with origin',
      'google.maps.Marker is deprecated',
      'ERR_CONNECTION_REFUSED',
      'GTM-TN3R6FZ3',
      // Stale-tab-after-deploy chunk load failures: recovered via chunkErrorDetected
      // (ReloadPromptPWA) instead of being reported — Sentry's own window.onerror/
      // onunhandledrejection and Vue's errorHandler capture these independently of
      // our addEventListener-based handlers, so preventDefault() alone can't stop them.
      'Failed to fetch dynamically imported module',
      'Importing a module script failed',
      // Transient SW update-check failures and blocked third-party analytics script —
      // browser/network-side noise, not application bugs.
      'Failed to update a ServiceWorker',
      'PageSense script failed to load'
    ],
    integrations: [
      new BrowserTracing({
        tracePropagationTargets: ['localhost', APP_URL, import.meta.env.VITE_APP_PRODUCTION_URL],
        routingInstrumentation: vueRouterInstrumentation(router)
      })
    ],
    tracesSampleRate: isProduction ? 0.1 : 1.0
  })

  // Separate automated-test events from real user errors: a CI run sets
  // window variables before init, a regular user doesn't have them — this branch doesn't run.
  const rid = globalThis.__SENTRY_TEST_RUN_ID__
  if (rid) {
    setTag('test_run_id', rid)
    setTag('test_title', globalThis.__SENTRY_TEST_TITLE__)
  }
}

// Google Maps calls this global callback asynchronously when the API key is invalid,
// restricted to the wrong domain, or billing isn't enabled — the script itself still
// loads with a 200, so this is NOT caught by the try/catch around loadGoogleMapsApi below.
if (typeof window !== 'undefined') {
  window.gm_authFailure = () => {
    captureException(new Error('Google Maps authentication failure (invalid/restricted API key)'), {
      tags: { integration: 'google_maps' },
      extra: { apiKey: import.meta.env.VITE_APP_GOOGLE_API_KEY ? 'present' : 'missing' }
    })
    console.error('[Google Maps] Authentication failure — check API key restrictions/billing.')
  }
}

let googleMapsLoaded = false
let googleMapsLoading = false
let googleMapsPluginInstalled = false

const loadGoogleMaps = async (retries = 3, delay = 2000) => {
  if (googleMapsLoaded || (typeof google !== 'undefined' && window.google?.maps)) {
    googleMapsLoaded = true
    return
  }
  if (googleMapsLoading) {
    // Wait for the current load to finish
    while (googleMapsLoading) {
      await new Promise((resolve) => setTimeout(resolve, 100))
    }
    return
  }

  googleMapsLoading = true
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      let observer = null
      const addAsyncToScript = () => {
        observer = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
              if (
                node.tagName === 'SCRIPT' &&
                node.src &&
                node.src.includes('maps.googleapis.com/maps/api/js')
              ) {
                const url = new URL(node.src)

                if (!url.searchParams.has('loading')) {
                  url.searchParams.set('loading', 'async')

                  if (!url.searchParams.has('callback')) {
                    url.searchParams.set('callback', 'googleMapsCallback')
                  }

                  node.src = url.toString()
                  node.async = true
                  node.defer = true
                }

                if (observer) observer.disconnect()
              }
            }
          }
        })

        observer.observe(document.head, { childList: true, subtree: true })

        setTimeout(() => {
          if (observer) observer.disconnect()
        }, 5000)
      }

      addAsyncToScript()

      await loadGoogleMapsApi({
        key: import.meta.env.VITE_APP_GOOGLE_API_KEY,
        libraries: ['places', 'drawing', 'geometry'],
        v: 'quarterly',
        language: 'en',
        timeout: 30000
      })

      if (observer) observer.disconnect()

      googleMapsLoaded = true
      googleMapsLoading = false

      // Install the VueGoogleMaps plugin after loading
      if (!googleMapsPluginInstalled) {
        app.use(VueGoogleMaps)
        googleMapsPluginInstalled = true
      }

      return
    } catch (err) {
      const isLastAttempt = attempt === retries
      const errorDetails = {
        attempt,
        totalRetries: retries,
        apiKey: import.meta.env.VITE_APP_GOOGLE_API_KEY ? 'present' : 'missing',
        error: err?.message || String(err)
      }

      if (isLastAttempt) {
        googleMapsLoading = false
        captureException(new Error('Failed to load Google Maps API after retries'), {
          extra: errorDetails
        })
        console.error('[Google Maps] Failed to load after', retries, 'attempts:', errorDetails)
        throw err
      } else {
        console.warn(`[Google Maps] Attempt ${attempt} failed, retrying in ${delay}ms...`)
        await new Promise((resolve) => setTimeout(resolve, delay))
      }
    }
  }
}

// Lazy-load Google Maps on user interaction or scroll
const initGoogleMapsLazy = () => {
  let initialized = false
  const init = () => {
    if (initialized) return
    initialized = true
    loadGoogleMaps().catch((err) => {
      console.error('Lazy Google Maps load failed', err)
    })
  }

  // Load on the first interaction
  const interactionEvents = ['mousedown', 'touchstart', 'keydown', 'scroll', 'click']
  interactionEvents.forEach((event) => {
    document.addEventListener(
      event,
      () => {
        init()
      },
      { passive: true, once: true }
    )
  })

  // Load on scroll (even without interaction) with a delay
  let scrollTimeout
  window.addEventListener(
    'scroll',
    () => {
      if (!initialized) {
        clearTimeout(scrollTimeout)
        scrollTimeout = setTimeout(() => {
          init()
        }, 2000) // Increase the delay to 2 seconds
      }
    },
    { passive: true }
  )
}

const startApp = async () => {
  try {
    await stylesReady
  } catch (err) {
    console.error('Styles load failed', err)
  }
  app.mount('#app')
  nextTick(() => {
    initGoogleMapsLazy()
  })
}

startApp()

nextTick(() => {
  let analyticsLoaded = false
  const initAnalytics = () => {
    if (!analyticsLoaded) {
      analyticsLoaded = true
      loadAnalytics()
    }
  }

  if ('requestIdleCallback' in window) {
    requestIdleCallback(initAnalytics, { timeout: 5000 })
  } else {
    setTimeout(initAnalytics, 5000)
  }

  const userInteractionEvents = ['mousedown', 'touchstart', 'keydown']
  userInteractionEvents.forEach((event) => {
    document.addEventListener(event, initAnalytics, { passive: true, once: true })
  })
})
